import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Mail, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";

export default function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success("Check your email for reset instructions");
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>
          
          <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
            <CardHeader className="space-y-1 pb-4">
              <div className="flex items-center gap-3 mb-2">
                <Logo size={40} className="text-primary" />
                <span className="text-xl font-semibold" >HeyIntake</span>
              </div>
              <CardTitle className="text-2xl" >
                {submitted ? "Check your email" : "Forgot password?"}
              </CardTitle>
              <CardDescription>
                {submitted 
                  ? "We've sent password reset instructions to your email address."
                  : "Enter your email and we'll send you a link to reset your password."
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <div className="space-y-6">
                  <div className="flex justify-center">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                  </div>
                  <div className="text-center space-y-2">
                    <p className="text-sm text-muted-foreground">
                      If an account exists for <span className="font-medium text-foreground">{email}</span>, you will receive an email with instructions.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      The link will expire in 1 hour.
                    </p>
                  </div>
                  <div className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full rounded-full"
                      onClick={() => {
                        setSubmitted(false);
                        setEmail("");
                      }}
                    >
                      Try another email
                    </Button>
                    <Link to="/login" className="block">
                      <Button className="w-full rounded-full">
                        Back to login
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="h-12 pl-10"
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full rounded-full h-12"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send reset link"
                    )}
                  </Button>
                </form>
              )}
              
              {!submitted && (
                <p className="text-center text-sm text-muted-foreground mt-6">
                  Remember your password?{" "}
                  <Link to="/login" className="text-primary font-medium hover:underline">
                    Sign in
                  </Link>
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
      
      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 bg-primary items-center justify-center p-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center text-white max-w-md"
        >
          <h2 className="text-4xl font-bold mb-4" >
            Don't worry, it happens!
          </h2>
          <p className="text-white/80 text-lg">
            We'll help you get back into your account in no time.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
